// Welcome / First Login Page — V1 invite-only (Priority 7 Day 5 full).
//
// URL: /onboarding/welcome?org_type=SO2&org_name=...&email=...
//
// Per onboarding-spec.md §3 (Modal #15) + §6 Step 6:
//   • 3-step role-aware tour (skippable)
//   • Getting-started checklist per org_type
//   • "Continue to Dashboard" → navigate to role-default dashboard
//
// V1 limitation: search params carry context because real auth (M3 resume)
// hasn't issued a JWT yet. Stage 5 backend resume → user state from session.

import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  CheckCircle2,
  Circle,
  ArrowRight,
  Mail,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  X,
  Compass,
  ListChecks,
} from 'lucide-react'
import { Card } from '../../components/ui/card'
import {
  ORG_TYPE_LABELS,
  SCOPE_LABELS,
  type OrgScope,
  type OrgTypeCode,
} from '../../lib/org-type-labels'
import { ROLE_TOURS, roleToursI18n } from './tour-content'
import { useT } from '../../i18n'
import { WELCOME_PAGE_STRINGS } from '../../i18n/onboarding/welcome'
import { markSellerTourPending } from './seller-tour-state'

// ─── Context extraction (URL search params) ──────────────────────────────────

type WelcomeContext = {
  orgType: OrgTypeCode
  orgName: string
  email: string
  orgScope: OrgScope
}

// BUG-11 fix: read context from sessionStorage (set by AcceptInvitationPage on
// successful accept). URL search params no longer carry sensitive email/org
// info — closes the privacy leak via URL bar, history, analytics tracking.
// Storage is scoped to current tab/session; cleared on tab close or explicit
// logout. Falls back to URL params for backward-compat (BUG-22 follow-up
// — typed search schema is still active but values default to undefined).
function readWelcomeContext(): WelcomeContext | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem('pexify_welcome_context')
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<{
        org_type: string
        org_name: string
        org_scope: string
        email: string
      }>
      if (
        parsed.org_type &&
        parsed.org_name &&
        parsed.email &&
        parsed.org_scope &&
        parsed.org_type in ROLE_TOURS
      ) {
        return {
          orgType: parsed.org_type as OrgTypeCode,
          orgName: parsed.org_name,
          email: parsed.email,
          orgScope: parsed.org_scope as OrgScope,
        }
      }
    }
  } catch {
    // sessionStorage corrupt / unavailable → fall through to null
  }
  return null
}

// ─── Component ────────────────────────────────────────────────────────────────

export function WelcomePage() {
  const t = useT(WELCOME_PAGE_STRINGS)
  const roleTours = useT(roleToursI18n)
  const navigate = useNavigate()
  const context = useMemo(() => readWelcomeContext(), [])
  const [tourStep, setTourStep] = useState(0)
  const [tourComplete, setTourComplete] = useState(false)
  const [checklistDone, setChecklistDone] = useState<Set<number>>(new Set())

  // No context = fallback / direct visit
  if (!context) {
    return (
      <div className="flex-1 bg-gradient-to-br from-slate-50 via-emerald-50/30 to-white flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 text-indigo-600">
              <Mail size={20} aria-hidden="true" />
              <span className="font-semibold text-lg tracking-tight">{t.brand}</span>
            </div>
          </div>
          <Card className="p-6 text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 size={28} className="text-emerald-600" aria-hidden="true" />
              </div>
            </div>
            <h1 className="text-lg font-semibold text-slate-900">{t.fallback.title}</h1>
            <p className="text-sm text-slate-600">
              {t.fallback.description}
            </p>
            <button
              type="button"
              onClick={() => navigate({ to: '/admin' })}
              className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded inline-flex items-center justify-center gap-1.5"
            >
              {t.fallback.cta}
              <ArrowRight size={14} aria-hidden="true" />
            </button>
          </Card>
        </div>
      </div>
    )
  }

  const tour = roleTours[context.orgType]
  const orgType = context.orgType
  const currentStep = tour.steps[tourStep]
  const isLastStep = tourStep === tour.steps.length - 1

  function nextStep() {
    if (isLastStep) {
      setTourComplete(true)
    } else {
      setTourStep((s) => s + 1)
    }
  }
  function prevStep() {
    setTourStep((s) => Math.max(0, s - 1))
  }
  function skipTour() {
    setTourComplete(true)
  }
  function toggleChecklist(i: number) {
    setChecklistDone((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  }
  function goToDashboard() {
    // BUG-11: clear sessionStorage so context doesn't linger beyond onboarding
    try {
      sessionStorage.removeItem('pexify_welcome_context')
    } catch {
      /* ignore */
    }
    if (orgType === 'BO2') {
      markSellerTourPending()
    }
    navigate({ to: tour.defaultRoute })
  }

  return (
    <div className="flex-1 bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center p-6">
      <div className="w-full max-w-2xl space-y-4">
        {/* Brand */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-indigo-600">
            <Mail size={20} aria-hidden="true" />
            <span className="font-semibold text-lg tracking-tight">{t.brand}</span>
          </div>
        </div>

        {/* Hero */}
        <Card className="p-6 space-y-3 bg-white">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 size={24} className="text-emerald-600" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-semibold text-slate-900">
                {t.hero.greeting(context.orgName)}
              </h1>
              <p className="text-sm text-slate-600 mt-0.5">{tour.tagline}</p>
              <div className="flex items-center gap-2 mt-2 text-[11px] flex-wrap">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded font-mono">
                  {context.orgType}
                </span>
                <span className="text-slate-300">·</span>
                <span className="text-slate-500">
                  {ORG_TYPE_LABELS[context.orgType]}
                </span>
                <span className="text-slate-300">·</span>
                <span className="text-slate-500">{SCOPE_LABELS[context.orgScope]}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Tour stepper (hidden after complete) */}
        {!tourComplete ? (
          <Card className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
                <Compass size={14} className="text-indigo-600" aria-hidden="true" />
                {t.tour.sectionTitle}
                <span className="text-[10px] text-slate-400 ml-1">
                  {t.tour.stepCounter(tourStep + 1, tour.steps.length)}
                </span>
              </h2>
              <button
                type="button"
                onClick={skipTour}
                className="text-[11px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1"
              >
                <X size={11} aria-hidden="true" />
                {t.tour.skipTour}
              </button>
            </div>

            {/* Step progress dots */}
            <div className="flex items-center gap-1.5">
              {tour.steps.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition ${
                    i <= tourStep ? 'bg-indigo-600' : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>

            {/* Step content */}
            <div className="bg-slate-50 border border-slate-200 rounded p-4 space-y-2 min-h-[140px]">
              <div className="flex items-center gap-2 text-[11px] text-indigo-700 font-medium uppercase tracking-wide">
                <Sparkles size={11} aria-hidden="true" />
                {t.tour.lookFor} <span className="px-1.5 py-0.5 bg-indigo-100 rounded text-indigo-800">
                  {currentStep.highlight}
                </span>
              </div>
              <h3 className="text-base font-semibold text-slate-900">
                {currentStep.title}
              </h3>
              <p className="text-sm text-slate-600 leading-snug">
                {currentStep.body}
              </p>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={prevStep}
                disabled={tourStep === 0}
                className="text-xs px-3 py-1.5 text-slate-500 hover:bg-slate-100 rounded disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              >
                <ChevronLeft size={12} aria-hidden="true" />
                {t.tour.back}
              </button>
              <button
                type="button"
                onClick={nextStep}
                className="text-xs px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded inline-flex items-center gap-1"
              >
                {isLastStep ? t.tour.finishTour : t.tour.next}
                {!isLastStep && <ChevronRight size={12} aria-hidden="true" />}
                {isLastStep && <CheckCircle2 size={12} aria-hidden="true" />}
              </button>
            </div>
          </Card>
        ) : (
          /* Post-tour CTA */
          <Card className="p-5 bg-emerald-50/40 border-emerald-200">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={24} className="text-emerald-600 flex-shrink-0" aria-hidden="true" />
              <div className="flex-1">
                <div className="text-sm font-medium text-emerald-900">{t.tourComplete.label}</div>
                <div className="text-xs text-emerald-700 mt-0.5">
                  {t.tourComplete.description}
                </div>
              </div>
              <button
                type="button"
                onClick={goToDashboard}
                className="text-sm px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium inline-flex items-center gap-1.5"
              >
                {t.tourComplete.cta}
                <ArrowRight size={14} aria-hidden="true" />
              </button>
            </div>
          </Card>
        )}

        {/* Getting-started checklist */}
        <Card className="p-5 space-y-3">
          <h2 className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            <ListChecks size={14} className="text-indigo-600" aria-hidden="true" />
            {t.checklist.sectionTitle}
            <span className="text-[10px] text-slate-400 ml-1">
              {t.checklist.progress(checklistDone.size, tour.checklist.length)}
            </span>
          </h2>
          <ul className="space-y-1.5">
            {tour.checklist.map((item, i) => {
              const done = checklistDone.has(i)
              return (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => toggleChecklist(i)}
                    className="w-full flex items-start gap-2 text-left p-2 hover:bg-slate-50 rounded transition group"
                  >
                    {done ? (
                      <CheckCircle2
                        size={16}
                        className="text-emerald-600 flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                    ) : (
                      <Circle
                        size={16}
                        className="text-slate-300 group-hover:text-indigo-400 flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-sm ${
                          done ? 'text-slate-400 line-through' : 'text-slate-800 font-medium'
                        }`}
                      >
                        {item.label}
                      </div>
                      <div className="text-[11px] text-slate-500 leading-snug">
                        {item.hint}
                      </div>
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
          <p className="text-[10px] text-slate-400 italic pt-1 border-t border-slate-100">
            {t.checklist.footnote}
          </p>
        </Card>

        {/* Always-available dashboard link */}
        <div className="text-center">
          <button
            type="button"
            onClick={goToDashboard}
            className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1"
          >
            {t.skipLink}
            <ArrowRight size={11} aria-hidden="true" />
          </button>
        </div>

        <p className="text-[10px] text-slate-400 text-center">
          {t.footer}
        </p>
      </div>
    </div>
  )
}
